import { IconDotsVertical } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';
import useAuthContext from 'contexts/AuthContext';
import React, { useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { formatUTCtoLocal, formatFileSize } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import EmptyState from 'components/EmptyState/EmptyState';
import { NSCard, NSModal, NSTable } from 'bricks';
import { IUploadedProForma } from 'types/db/UploadedProForma';
import { headerCellClassName } from '../../constants';
import { URLS } from '../../../../services/urls';
import { useDeleteUploadedProForma } from '../../../../queries/UploadedProForma';
import { getProFormaDisplayName } from 'views/ProFormaUpload/helpers';
import SortingIcon from 'components/DraggableTable/DraggableHeader/SortingIcon/SortingIcon';
import {
    getSortingIconProps,
    sortItems,
    SortConfig,
} from '../../helpers';

export interface IManualSpreadsheetTableProps {
    isLoading?: boolean;
    uploadedProFormas: IUploadedProForma[];
    dealId?: number;
    isAllDealsProFormasPage?: boolean;
}

const ManualSpreadsheetTable = ({
    uploadedProFormas,
    isLoading,
    dealId: dealIdProp,
    isAllDealsProFormasPage = false,
}: IManualSpreadsheetTableProps) => {
    const navigate = useNavigate();
    const { dealId: dealIdFromParams } = useParams<{ dealId: string }>();
    const dealId = dealIdProp || Number(dealIdFromParams);
    const { selectedOrganizationId } = useAuthContext();
    const [sortedBy, setSortedBy] = useState<SortConfig | null>(null);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [proFormaToDelete, setProFormaToDelete] = useState<number>();

    const { mutate: deleteUploadedProForma } = useDeleteUploadedProForma();

    const handleRowClick = (id: number, shouldEdit: boolean = false) => {
        const path = generatePath(URLS.PRO_FORMA.UPLOAD, {
            dealId: Number(dealId),
            proFormaId: id,
        });

        if (shouldEdit) {
            navigate(`${path}?shouldEdit=true`);
        } else {
            navigate(path);
        }
    };

    // Resolve the display name onto each row so the Name column sorts by what's shown, not the raw s3 path.
    const proFormasWithDisplayName = React.useMemo(
        () => (uploadedProFormas || []).map(proForma => ({ ...proForma, displayName: getProFormaDisplayName(proForma) })),
        [uploadedProFormas],
    );

    const sortedProFormas = React.useMemo(() => sortItems(proFormasWithDisplayName, sortedBy, sortedBy?.key || ''), [proFormasWithDisplayName, sortedBy]);

    const handleConfirmDelete = () => {
        deleteUploadedProForma({
            organizationId: selectedOrganizationId as string,
            uploadedProFormaId: proFormaToDelete as number,
        });
        setShowDeleteConfirmationModal(false);
    };

    const handleEllipsisOption = (option: 'Edit' | 'Delete', id: number) => {
        if (option === 'Edit') handleRowClick(id, true);
        if (option === 'Delete') {
            setProFormaToDelete(id);
            setShowDeleteConfirmationModal(true);
        }
    };

    return (
        <>
            <RenderIf isTrue={uploadedProFormas?.length === 0} isLoading={isLoading}>
                <EmptyState
                    title="No pro formas uploaded yet!"
                    className={isAllDealsProFormasPage ? 'pt-2 pb-1' : 'vh-100'}
                    titleClassName={isAllDealsProFormasPage ? 'text-muted h6' : undefined}
                />
            </RenderIf>
            <RenderIf isTrue={uploadedProFormas?.length !== 0}>
                <NSTable>
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            <th className="NSTable__thead__tr__th text-dark">
                                <div className={headerCellClassName}>
                                    <span>Name</span>
                                    <SortingIcon {...getSortingIconProps('displayName', sortedBy, setSortedBy)} />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th">
                                <div className={headerCellClassName}>
                                    <span>Uploaded</span>
                                    <SortingIcon {...getSortingIconProps('createdAt', sortedBy, setSortedBy)} />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th">
                                <div className={headerCellClassName}>
                                    <span>Uploaded by</span>
                                    <SortingIcon {...getSortingIconProps('createdBy', sortedBy, setSortedBy)} />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th">
                                <div className={headerCellClassName}>
                                    <span>Size</span>
                                    <SortingIcon {...getSortingIconProps('fileSize', sortedBy, setSortedBy)} />
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedProFormas?.map(item => (
                            <tr
                                key={item.id}
                                className="NSTable__tbody__tr text-dark hover:bg-gray-100"
                            >
                                <td className="NSTable__tbody__tr__td text-primary">
                                    <span
                                        role="presentation"
                                        className="cursor--pointer text-primary"
                                        onClick={() => handleRowClick(item.id)}
                                    >
                                        {getProFormaDisplayName(item)}
                                    </span>
                                </td>
                                <td className="NSTable__tbody__tr__td">
                                    {formatUTCtoLocal(item.createdAt, 'M/d/yy h:mm a')}
                                </td>
                                <td className="NSTable__tbody__tr__td">
                                    {item.createdBy || 'N/A'}
                                </td>
                                <td className="d-flex justify-content-between align-items-center">
                                    {item.fileSize ? formatFileSize(item.fileSize) : 'N/A'}
                                    <RenderIf isTrue={!isAllDealsProFormasPage}>
                                        <UncontrolledDropdown>
                                            <DropdownToggle tag="div">
                                                <NSIcon icon={IconDotsVertical} className="cursor--pointer" role="button" />
                                            </DropdownToggle>
                                            <DropdownMenu container="body" className="p-0 m-0 bg-transparent z-index-100">
                                                <NSCard className="NSCard--level-5 shadow-none">
                                                    <DropdownItem
                                                        onClick={() => handleEllipsisOption('Edit', item.id)}
                                                        className="text-dark"
                                                    >
                                                        Edit
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        onClick={() => handleEllipsisOption('Delete', item.id)}
                                                        className="text-dark"
                                                    >
                                                        Delete
                                                    </DropdownItem>
                                                </NSCard>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </RenderIf>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </NSTable>
            </RenderIf>

            <NSModal
                isOpen={showDeleteConfirmationModal}
                toggle={() => setShowDeleteConfirmationModal(false)}
                modalHeaderText="Delete pro forma"
                modalHeaderClassName="text-white bg-danger"
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
                modalFooterCancelButtonText="Cancel"
                modalFooterButtonFunction={handleConfirmDelete}
            >
                <div className="mb-1">
                    <div className="text-white">Are you sure you want to delete this pro forma?</div>
                </div>
            </NSModal>
        </>
    );
};

export default ManualSpreadsheetTable;
